/**
 * A coach's turnaround counted in their own working days. The review is due at
 * the end of the Nth working day after the day the player sends; the coach must
 * answer within the accept window counted from the first working moment. Pure
 * over a `DayClock`, so the server counts days in London time and the tests
 * count them in UTC.
 */

import type { Weekday } from "./types";

export const WEEKDAYS: readonly Weekday[] = [1, 2, 3, 4, 5];
export const EVERY_DAY: readonly Weekday[] = [0, 1, 2, 3, 4, 5, 6];
export const WEEKDAY_LABELS: Record<Weekday, string> = { 0: "Sun", 1: "Mon", 2: "Tue", 3: "Wed", 4: "Thu", 5: "Fri", 6: "Sat" };

export interface DayClock {
    dayOfWeek(ms: number): Weekday;
    /** The first instant of the calendar day containing `ms`. */
    startOfDay(ms: number): number;
}

const DAY_MS = 24 * 60 * 60 * 1000;
const HOUR_MS = 60 * 60 * 1000;

export const utcDayClock: DayClock = {
    dayOfWeek: (ms) => new Date(ms).getUTCDay() as Weekday,
    startOfDay: (ms) => Math.floor(ms / DAY_MS) * DAY_MS,
};

/** A clock for one IANA zone, from Intl: right across DST changes. */
export function zonedDayClock(timeZone: string): DayClock {
    const fmt = new Intl.DateTimeFormat("en-GB", { timeZone, weekday: "short", year: "numeric", month: "numeric", day: "numeric", hour: "numeric", minute: "numeric", second: "numeric", hourCycle: "h23" });
    const DAYS: Record<string, Weekday> = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
    const parts = (ms: number) => {
        const out: Record<string, string> = {};
        for (const p of fmt.formatToParts(new Date(ms))) if (p.type !== "literal") out[p.type] = p.value;
        return out;
    };
    return {
        dayOfWeek: (ms) => DAYS[parts(ms).weekday] ?? 0,
        startOfDay: (ms) => {
            const p = parts(ms);
            // The wall-clock time since local midnight, then step back by it.
            const sinceMidnight = (Number(p.hour) * 3600 + Number(p.minute) * 60 + Number(p.second)) * 1000;
            return ms - sinceMidnight;
        },
    };
}

export function isWeekday(v: unknown): v is Weekday {
    return typeof v === "number" && Number.isInteger(v) && v >= 0 && v <= 6;
}

/** A working-day list from client data: distinct, sorted, never empty (an empty list means every day). */
export function normaliseWorkingDays(raw: unknown): Weekday[] {
    if (!Array.isArray(raw)) return [...EVERY_DAY];
    const days = [...new Set(raw.filter(isWeekday))].sort((a, b) => a - b);
    return days.length === 0 ? [...EVERY_DAY] : days;
}

/** The first instant on or after `ms` that falls on a working day. */
export function nextWorkingMoment(ms: number, workingDays: readonly Weekday[], clock: DayClock): number {
    let t = ms;
    for (let i = 0; i < 14; i++) {
        if (workingDays.includes(clock.dayOfWeek(t))) return t;
        t = clock.startOfDay(t) + DAY_MS;
    }
    return ms;
}

/**
 * When the review is due: the end of the Nth working day after the day of
 * sending. Sent on a Friday with three working days Monday to Friday, that is
 * Wednesday night; sent on a Monday, Thursday night.
 */
export function dueAfterWorkingDays(submittedAtMs: number, turnaroundDays: number, workingDays: readonly Weekday[], clock: DayClock): number {
    const days = Math.max(1, Math.round(turnaroundDays));
    let dayStart = clock.startOfDay(submittedAtMs);
    let counted = 0;
    for (let i = 0; i < 60 && counted < days; i++) {
        dayStart = clock.startOfDay(dayStart + DAY_MS);
        if (workingDays.includes(clock.dayOfWeek(dayStart))) counted++;
    }
    return clock.startOfDay(dayStart + DAY_MS) - 1;
}

/** When the coach must have answered: the accept window from the first working moment, never past the due time. */
export function respondByWorkingDays(submittedAtMs: number, acceptWithinHours: number, dueAtMs: number, workingDays: readonly Weekday[], clock: DayClock): number {
    const from = nextWorkingMoment(submittedAtMs, workingDays, clock);
    return Math.min(dueAtMs, from + acceptWithinHours * HOUR_MS);
}

/** "3 working days", or "3 days" when the coach works every day. */
export function turnaroundText(turnaroundDays: number, workingDays: readonly Weekday[]): string {
    const n = Math.max(1, Math.round(turnaroundDays));
    const unit = workingDays.length >= 7 ? "day" : "working day";
    return `${n} ${unit}${n === 1 ? "" : "s"}`;
}

/** "Mon to Fri", "Mon, Wed, Fri", "every day". */
export function workingDaysText(workingDays: readonly Weekday[]): string {
    const days = [...new Set(workingDays)].sort((a, b) => a - b);
    if (days.length >= 7) return "every day";
    const consecutive = days.every((d, i) => i === 0 || d === days[i - 1] + 1);
    if (consecutive && days.length >= 3) return `${WEEKDAY_LABELS[days[0]]} to ${WEEKDAY_LABELS[days[days.length - 1]]}`;
    return days.map((d) => WEEKDAY_LABELS[d]).join(", ");
}
